import { gql } from '@apollo/client';
import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useRef, useState } from 'react';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { OrganizationSsoIndexPageDocument } from '~pages/[organization]/settings/sso/__generated__/Index.graphql';
import { useSamlGenerateEntityIdMutation } from './__generated__/ChooseSsoServiceModal.graphql';
import { OidcInitialData } from './OidcRegistrationModal';
import { SamlInitialData } from './SamlRegistrationModal';
import { SSO_SERVICE_TO_STRATEGY, SsoServiceType } from './SsoTypes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onNext: (service: SsoServiceType, initialData: OidcInitialData | SamlInitialData | null) => void;
  ssoId: string;
  domain: string;
}

export function ChooseSsoServiceModal({ isOpen, onClose, onNext, ssoId, domain }: Props) {
  const initialFocusRef = useRef<HTMLInputElement | null>(null);
  const [selectedService, setSelectedService] = useState<SsoServiceType | null>(null);
  const toast = useToast();

  const [generateEntityId, { loading }] = useSamlGenerateEntityIdMutation({
    refetchQueries: [{ query: OrganizationSsoIndexPageDocument }],
    awaitRefetchQueries: true,
  });

  const handleNext = async () => {
    if (!selectedService) return;

    try {
      const strategy = SSO_SERVICE_TO_STRATEGY[selectedService];

      switch (strategy) {
        case 'oidc': {
          onNext(selectedService, { connectionName: domain, clientId: '' });
          break;
        }

        case 'samlp': {
          const { data } = await generateEntityId({ variables: { id: ssoId } });
          const options = data?.ssoProviderSamlGenerateEntityId.provider?.options;

          if (options?.__typename !== 'SamlOptions' || !options.entityId) {
            throw new Error('Failed to generate name for SAML SSO provider');
          }

          onNext(selectedService, { connectionName: domain, entityId: options.entityId });
          break;
        }
      }
    } catch (e) {
      toast({
        title: t('ssoProviderNameGenerationFailed'),
        status: 'error',
      });
    }
  };

  const handleClose = () => {
    setSelectedService(null);
    onClose();
  };

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('chooseSsoService')}</ModalHeader>
        <ModalCloseButton onClick={handleClose} />

        <ModalBody>
          <RadioGroup
            value={selectedService || ''}
            onChange={(val) => setSelectedService(val as SsoServiceType)}
          >
            <Stack spacing={3}>
              {/* OIDC Option */}
              <Box
                borderWidth="1px"
                borderRadius="md"
                borderColor={selectedService === SsoServiceType.OIDC ? 'blue.500' : 'gray.200'}
                bg={selectedService === SsoServiceType.OIDC ? 'blue.50' : 'white'}
                px={4}
                py={3}
                cursor="pointer"
                onClick={() => setSelectedService(SsoServiceType.OIDC)}
                transition="all 0.2s"
              >
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="medium">
                    {SsoServiceType.OIDC}
                  </Text>
                  <Radio
                    value={SsoServiceType.OIDC}
                    ref={initialFocusRef}
                    colorScheme="blue"
                    isChecked={selectedService === SsoServiceType.OIDC}
                  />
                </HStack>
              </Box>

              {/* SAML Option */}
              <Box
                borderWidth="1px"
                borderRadius="md"
                borderColor={selectedService === SsoServiceType.SAML ? 'blue.500' : 'gray.200'}
                bg={selectedService === SsoServiceType.SAML ? 'blue.50' : 'white'}
                px={4}
                py={3}
                cursor="pointer"
                onClick={() => setSelectedService(SsoServiceType.SAML)}
                transition="all 0.2s"
              >
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="medium">
                    {SsoServiceType.SAML}
                  </Text>
                  <Radio
                    value={SsoServiceType.SAML}
                    colorScheme="blue"
                    isChecked={selectedService === SsoServiceType.SAML}
                  />
                </HStack>
              </Box>
            </Stack>
          </RadioGroup>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" colorScheme="gray" onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="solid"
            colorScheme="blue"
            marginLeft="3"
            onClick={handleNext}
            isDisabled={!selectedService}
            isLoading={loading}
          >
            {t('next')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

ChooseSsoServiceModal.graphql = {
  mutations: {
    SamlGenerateEntityId: gql`
      mutation SamlGenerateEntityId($id: ID!) {
        ssoProviderSamlGenerateEntityId(input: { id: $id }) {
          id
          domain
          verificationStatus
          provider {
            ... on SsoProviderInterface {
              strategy
              validationStatus
            }
            ... on OidcProvider {
              options {
                clientId
              }
            }
            ... on SamlProvider {
              options {
                entityId
              }
            }
          }
        }
      }
    `,
  },
};
